"use client";

import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import { Body } from "@/client/foundations/Typography";

interface AccordionProps {
  id?: string;
  heading: string;
  expandedBody?: ReactNode | undefined;
  headingContainer?: ReactNode | undefined;
  headerFontSize?: "xxs" | "xs" | "s" | "m" | "l" | "xl";
  fontStyle?: string;
  groupName?: string;
}

const Accordion = ({
  headingContainer,
  expandedBody,
  heading,
  id,
  headerFontSize = "l",
  ...props
}: AccordionProps) => {
  const [toggleAccord, setToggleAccord] = useState(false);

  const isInContentfulStudio =
    typeof window !== "undefined" &&
    window.self !== window.top &&
    window?.document?.getElementsByClassName(
      "styles-module_tooltipWrapper__kqvmR"
  )?.length > 0;

  const toggleAccordion = () => {
    action();
    setToggleAccord(!toggleAccord);
  };

  // SVG for minus icon
  const minusSVG = (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 16 16"
      className="w-4 h-4"
      fill="currentColor"
    >
      <path d="M3.75 7.25a.75.75 0 0 0 0 1.5h8.5a.75.75 0 0 0 0-1.5h-8.5Z" />
    </svg>
  );

  // SVG for Plus icon
  const plusSVG = (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 16 16"
      className="w-4 h-4"
      fill="currentColor"
    >
      <path d="M8.75 3.75a.75.75 0 0 0-1.5 0v3.5h-3.5a.75.75 0 0 0 0 1.5h3.5v3.5a.75.75 0 0 0 1.5 0v-3.5h3.5a.75.75 0 0 0 0-1.5h-3.5v-3.5Z" />
    </svg>
  );

  const accordionHandler = useCallback(
    (e: CustomEvent<{ data: string }>) => {
      if (e.detail.data === props?.groupName && toggleAccord) {
        setToggleAccord(false);
      }
    },
    [toggleAccord, props?.groupName]
  );

  useEffect(() => {
    window.addEventListener("accordionEvent", accordionHandler as EventListener);
    return () => {
      window.removeEventListener("accordionEvent", accordionHandler as EventListener);
    };
  }, [accordionHandler]);

  const action = () => {
    window.dispatchEvent(
      new CustomEvent("accordionEvent", { detail: { data: props?.groupName } })
    );
  };

  return (
    <div id={id || "accord"} data-testid="Accordion">
      <div className="border-accordion-border">
        <div
          className="flex justify-between flex-row cursor-pointer items-center"
          onClick={toggleAccordion}
        >
          {heading ? (
            <Body
              fontStyle={props?.fontStyle}
              size={headerFontSize}
              // className="text-display-s sm:text-display-s-sm md:text-display-s-md xl:text-display-s-xl px-accordHeadingX py-accordHeadingY"
              className={classNames("px-accordHeadingX py-accordHeadingY")}
            >
              {heading}
            </Body>
          ) : (
            <div className={"w-full"}>{headingContainer}</div>
          )}

          <span
            id="icon-1"
            className="text-slate-800 transition-transform duration-300 px-accordExpandCollapseIconX" 
          >
            {toggleAccord ? minusSVG : plusSVG}
          </span>
        </div>
        <div
          className={
            "w-full " +
            (() => {
              return !isInContentfulStudio ? classNames(
                    `transition-all ease delay-0 duration-600`,
                    `flex justify-between pt-1 ${
                      toggleAccord ? "max-h-[1500px]" : "max-h-[0] overflow-hidden"
                      // toggleAccord ? "h-[auto]" : "collapse"
                    }`
                  )
                : "block";
            })()
          }
        >
          {expandedBody}
        </div>
      </div>
    </div>
  );
};

export default Accordion;
